import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

export default function Settings() {
  const { user, signOut } = useAuth();
  const [profile, setProfile] = useState<any>(null);
  const [fullName, setFullName] = useState("");
  const [packages, setPackages] = useState<any[]>([]);
  const [userPackages, setUserPackages] = useState<any[]>([]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data: p } = await supabase.from("profiles").select("*").eq("id", user.id).maybeSingle();
      setProfile(p);
      setFullName(p?.full_name || "");
      const { data: pkgs } = await supabase.from("packages").select("*").eq("active", true).order("sort_order");
      setPackages(pkgs || []);
      const { data: up } = await supabase.from("user_packages").select("*, packages(*)").eq("user_id", user.id);
      setUserPackages(up || []);
    })();
  }, [user]);

  async function saveProfile() {
    if (!user) return;
    const { error } = await supabase.from("profiles").update({ full_name: fullName }).eq("id", user.id);
    if (error) toast.error(error.message);
    else toast.success("Profile updated");
  }

  const activePkgIds = new Set(userPackages.filter((u) => !u.expires_at || new Date(u.expires_at) > new Date()).map((u) => u.package_id));

  return (
    <div className="space-y-8 max-w-3xl mx-auto">
      <div>
        <h1 className="text-3xl font-bold">Settings</h1>
        <p className="text-muted-foreground">Manage your profile and learning plan.</p>
      </div>

      <Card>
        <CardHeader><CardTitle>Profile</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Email</Label>
            <Input value={user?.email || ""} disabled />
          </div>
          <div>
            <Label>Full name</Label>
            <Input value={fullName} onChange={(e) => setFullName(e.target.value)} />
          </div>
          <div className="flex gap-2">
            <Button onClick={saveProfile}>Save</Button>
            <Button variant="outline" onClick={signOut}>Sign out</Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Your plan</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {packages.map((p) => {
              const isActive = activePkgIds.has(p.id);
              return (
                <Card key={p.id} className={p.highlight ? "border-primary" : ""}>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg">{p.name}</CardTitle>
                      {isActive && <Badge>Active</Badge>}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="text-2xl font-bold">
                      ${(p.price_cents / 100).toFixed(2)}
                      <span className="text-sm font-normal text-muted-foreground">/{p.billing_period}</span>
                    </div>
                    <ul className="text-sm space-y-1">
                      {(p.features || []).map((f: string, i: number) => <li key={i}>• {f}</li>)}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          <p className="text-xs text-muted-foreground">
            Need to upgrade or manage payments? Contact support.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
